/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

type DeleteAlertConfigMutationConfig = {
  onSuccess?: () => void;
  onError?: (error: ApiError) => void;
};

export function useDeleteAlertConfigMutation({ onSuccess, onError }: DeleteAlertConfigMutationConfig = {}) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const queryClient = useQueryClient();

  const { mutate, isError, error, isPending } = useMutation<void, ApiError, string>({
    mutationFn: (id: string) => {
      return mgmtPlaneAPIClient.alerts.deleteAlert({
        tenantId: selectedTenantId!,
        id,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['alertConfigs'] });
      queryClient.invalidateQueries({ queryKey: ['alertConfig', selectedTenantId, variables] });
      onSuccess?.();
    },
    onError,
  });

  return { mutate, isError, error, isPending };
}
